import React from 'react';
import './Table.scss';

export interface TableColumn<T> {
  key: string;
  header: React.ReactNode;
  render?: (item: T, index: number) => React.ReactNode;
  accessor?: (item: T) => any;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
  };
  onRowClick?: (item: T, index: number) => void;
  className?: string;
  variant?: 'default' | 'striped' | 'bordered';
}

// Skeleton loading component
const TableSkeleton: React.FC<{ columns: number; rows?: number }> = ({ 
  columns, 
  rows = 5 
}) => (
  <div className="table-skeleton">
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="table-skeleton__row">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div 
            key={colIndex} 
            className="table-skeleton__cell"
            style={{ 
              animationDelay: `${(rowIndex * columns + colIndex) * 50}ms` 
            }}
          >
            <div className="table-skeleton__bar" />
          </div>
        ))}
      </div>
    ))}
  </div>
);

// Empty state component
const TableEmpty: React.FC<{ message: string; icon?: React.ReactNode }> = ({ 
  message, 
  icon 
}) => (
  <div className="table-empty">
    <div className="table-empty__icon">
      {icon || (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          <path d="M9 14l2 2 4-4" opacity="0.5" />
        </svg>
      )}
    </div>
    <p className="table-empty__message">{message}</p>
  </div>
);

export function Table<T extends Record<string, any>>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
  emptyIcon,
  pagination,
  onRowClick,
  className = '',
  variant = 'default',
}: TableProps<T>) {
  const renderCell = (column: TableColumn<T>, item: T, index: number) => {
    if (column.render) {
      return column.render(item, index);
    }
    if (column.accessor) {
      return column.accessor(item);
    }
    return item[column.key] ?? '-';
  };

  const handlePageChange = (page: number) => {
    if (pagination && page >= 1 && page <= pagination.totalPages) {
      pagination.onPageChange(page);
    }
  };

  const getPageNumbers = (): (number | string)[] => {
    if (!pagination) return [];
    
    const { currentPage, totalPages } = pagination;
    let currPage = Number(currentPage);
    // If 7 or fewer pages, show all
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    if (currPage <= 4) {
      // Near the start: 1, 2, 3, 4, 5, ..., last
      pages.push(2, 3, 4, 5);
      pages.push('...');
      pages.push(totalPages);
    } else if (currPage >= totalPages - 3) {
      // Near the end: 1, ..., last-4, last-3, last-2, last-1, last
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // In the middle: 1, ..., current-1, current, current+1, ..., last
      pages.push('...');
      pages.push(currPage - 1);
      pages.push(currPage);
      pages.push(currPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  const startEntry = pagination 
    ? ((pagination.currentPage - 1) * pagination.pageSize) + 1 
    : 1;
  const endEntry = pagination 
    ? Math.min(pagination.currentPage * pagination.pageSize, pagination.total) 
    : data.length;

  return (
    <div className={`table-container table-container--${variant} ${className}`}>
      <div className="table-wrapper">
        <table className="table">
          <thead className="table__head">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="table__header-cell"
                  style={{
                    width: column.width,
                    textAlign: column.align || 'left',
                  }}
                >
                  <span className="table__header-text">{column.header}</span>
                </th>
              ))}
            </tr>
          </thead>
          
          {!isLoading && data.length > 0 && (
            <tbody className="table__body">
              {data.map((item, index) => (
                <tr
                  key={index}
                  className={`table__row ${onRowClick ? 'table__row--clickable' : ''}`}
                  onClick={() => onRowClick?.(item, index)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="table__cell"
                      style={{ textAlign: column.align || 'left' }}
                    >
                      {renderCell(column, item, index)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>

        {isLoading && <TableSkeleton columns={columns.length} rows={5} />}
        {!isLoading && data.length === 0 && (
          <TableEmpty message={emptyMessage} icon={emptyIcon} />
        )}
      </div>

      {pagination && pagination.totalPages > 0 && (
        <div className="table__pagination">
          <div className="table__pagination-info">
            <span className="table__pagination-showing">
              Showing <strong>{startEntry}</strong> to <strong>{endEntry}</strong> of <strong>{pagination.total}</strong> entries
            </span>
          </div>

          <div className="table__pagination-controls">
            <button
              className="table__pagination-btn table__pagination-btn--nav"
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage === 1}
              title="First page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </svg>
            </button>
            
            <button
              className="table__pagination-btn table__pagination-btn--nav"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              title="Previous page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div className="table__pagination-pages">
              {getPageNumbers().map((page, index) => {
                if (page === '...') {
                  return (
                    <span key={`ellipsis-${index}`} className="table__pagination-ellipsis">
                      ···
                    </span>
                  );
                }
                const pageNumber = Number(page);
                const isActive = pageNumber === Number(pagination.currentPage);
                return (
                  <button
                    key={`page-${pageNumber}-${index}`}
                    className={`table__pagination-btn table__pagination-btn--page ${isActive ? 'table__pagination-btn--active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className="table__pagination-btn table__pagination-btn--nav"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              title="Next page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            <button
              className="table__pagination-btn table__pagination-btn--nav"
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
              title="Last page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="13 17 18 12 13 7" />
                <polyline points="6 17 11 12 6 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
